import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { DeepLinkService } from '../deep-link.service';
import { NotificationService } from '../notification/notification.service';

/** Header list fallback when app_update API has no `title` (not used for FCM push). */
export const APP_UPDATE_NOTIFICATION_TITLE = 'Update Available! 🚀';

/** FCM `data` payload keys (all string values on native). */
export interface PushNotificationData {
  id?: string;
  type?: string;
  route?: string;
  slug?: string;
  store_url?: string;
  version?: string;
  update_type?: string;
  event_slug?: string;
  action_id?: string;
  click_action?: string;
  link?: string;
  message?: string;
  [key: string]: string | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class PushNavigationService {
  private readonly router = inject(Router);
  private readonly deepLinkService = inject(DeepLinkService);
  /** Lazy resolve to avoid NG0200: NotificationService ↔ PushService ↔ PushNavigationService */
  private readonly injector = inject(Injector);

  /**
   * Navigate from FCM data (notification tap or cold start).
   * Returns true when a route was resolved and navigation was attempted.
   */
  navigateFromPushData(raw: Record<string, unknown> | undefined): boolean {
    if (!raw || typeof raw !== 'object') {
      return false;
    }

    const data = this.normalizeData(raw);
    if (PushNavigationService.isAppUpdateType(data.type)) {
      if (this.openAppUpdateStore(data)) {
        return true;
      }
      const route = data.route?.trim();
      if (route) {
        const path = this.normalizeAppPath(route);
        const navigate = () => this.performNavigation(path, data);
        if (Capacitor.isNativePlatform()) {
          setTimeout(navigate, 150);
        } else {
          navigate();
        }
        return true;
      }
      return false;
    }

    const path = this.resolvePath(data);

    if (!path) {
      return this.openExternalLink(data);
    }

    const navigate = () => this.performNavigation(path, data);
    // Brief delay so router is ready on cold start from notification tap.
    if (Capacitor.isNativePlatform()) {
      setTimeout(navigate, 150);
    } else {
      navigate();
    }
    return true;
  }

  /** Resolve in-app path from push `data` (same rules as header notification list). */
  resolvePath(data: PushNotificationData): string | null {
    const type = (data.type || '').trim();
    if (PushNavigationService.isAppUpdateType(type)) {
      return null;
    }

    const route = data.route?.trim();
    if (route) {
      return this.normalizeAppPath(route);
    }
    const chatTypes = ['group_chat', 'meeting_join_req', 'chat'];

    if (chatTypes.includes(type) && data.action_id) {
      return `/chat-detail/${data.action_id}`;
    }

    if (
      (type === 'event_join_req' || data.event_slug) &&
      data.event_slug
    ) {
      return `/activity/${this.stripSlashes(data.event_slug)}`;
    }

    const trainingTypes = [
      'training_booked',
      'training_completed',
      'training_cancelled',
    ];
    if (trainingTypes.includes(type)) {
      const slugPath = this.normalizeSlugPath(data.slug || '');
      if (slugPath) {
        return `/${slugPath}`;
      }
    }

    if (data.event_slug) {
      return `/activity/${this.stripSlashes(data.event_slug)}`;
    }

    if (data.slug) {
      const slugPath = this.normalizeSlugPath(data.slug);
      if (slugPath) {
        return `/${slugPath}`;
      }
    }

    return null;
  }

  private performNavigation(path: string, data: PushNotificationData): void {
    const notificationService = this.injector.get(NotificationService);

    const notificationId = data.id ? parseInt(data.id, 10) : NaN;
    if (!Number.isNaN(notificationId) && this.isAuthenticated()) {
      void notificationService
        .markNotificationAsRead(notificationId)
        .catch(() => {});
    }

    if (!this.isAuthenticated()) {
      this.deepLinkService.setPendingDeepLinkPath(path);
      void this.router.navigate(['/signin']);
      return;
    }

    void this.router.navigateByUrl(path).catch(() => {
      void this.router.navigate(['/home']);
    });

    notificationService.refreshNotifications();
  }

  static isAppUpdateType(type?: string): boolean {
    return (type || '').trim().toLowerCase() === 'app_update';
  }

  static normalizeExternalUrl(raw?: string | null): string | null {
    if (!raw || typeof raw !== 'string') return null;
    const url = raw.replace(/\\\//g, '/').trim();
    return /^https?:\/\//i.test(url) ? url : null;
  }

  /** External store link from API list item or FCM `data` (store_url or slug when URL). */
  static resolveAppUpdateStoreUrl(
    item: { type?: string; store_url?: string; slug?: string }
  ): string | null {
    if (!PushNavigationService.isAppUpdateType(item.type)) return null;
    return (
      PushNavigationService.normalizeExternalUrl(item.store_url) ||
      PushNavigationService.normalizeExternalUrl(item.slug)
    );
  }

  /** FCM / push title: `notification.title`, then `data.title`. */
  static resolvePushTitle(payload: {
    notification?: { title?: string };
    data?: Record<string, string | undefined>;
  }): string {
    const data = payload.data || {};
    return (payload.notification?.title || data['title'] || 'New Notification').trim();
  }

  /** FCM / push body: `notification.body`, then `data.body`, then `data.message`. */
  static resolvePushBody(payload: {
    notification?: { body?: string };
    data?: Record<string, string | undefined>;
  }): string {
    const data = payload.data || {};
    return (payload.notification?.body || data['body'] || data['message'] || '').trim();
  }

  openAppUpdateStore(data: PushNotificationData): boolean {
    const url = PushNavigationService.resolveAppUpdateStoreUrl(data);
    if (!url || typeof window === 'undefined') {
      return false;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }

  private openExternalLink(data: PushNotificationData): boolean {
    const appStore = PushNavigationService.resolveAppUpdateStoreUrl(data);
    if (appStore) {
      return this.openAppUpdateStore(data);
    }

    const url = (data.click_action || data.link || '').trim();
    if (!url) {
      return false;
    }
    if (url.startsWith('https://theselfwish.com')) {
      try {
        const path = new URL(url).pathname;
        if (path && path !== '/') {
          return this.navigateFromPushData({ route: path });
        }
      } catch {
        /* ignore */
      }
    }
    if (url.startsWith('selfwish://')) {
      this.deepLinkService.handleUrl(url);
      return true;
    }
    return false;
  }

  private normalizeData(
    raw: Record<string, unknown>
  ): PushNotificationData {
    const data: PushNotificationData = {};
    for (const [key, value] of Object.entries(raw)) {
      if (value !== undefined && value !== null) {
        data[key] = String(value);
      }
    }
    return data;
  }

  private normalizeAppPath(route: string): string {
    let path = route.trim();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    path = path.replace(/\/{2,}/g, '/');
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    return path;
  }

  private normalizeSlugPath(slug: string): string | null {
    if (!slug?.trim()) {
      return null;
    }
    return slug.replace(/\\\//g, '/').replace(/^\/+/, '').trim() || null;
  }

  private stripSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
  }

  private isAuthenticated(): boolean {
    return (
      typeof localStorage !== 'undefined' && !!localStorage.getItem('token')
    );
  }
}
